import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import {
  useClientMetricsQuery,
  usePaymentCarrierListNameQuery,
} from '../../../api/payment-assignment-api-client'
import type { GetPaymentMetricsResponseDto } from '../../../api/dto/get-payment-assignment-metrics.dto'
import CompanyPerspectiveMetrics from './CompanyPerspectiveMetrics'
import CarrierPerspectiveMetrics from './CarrierPerspectiveMetrics'
import { endOfDayPST, startOfDayPST } from 'helpers/datetime'

interface CustomerMetricsProps {
  paymentMetricsData: GetPaymentMetricsResponseDto | undefined
  loading?: boolean
  fromDate?: dayjs.Dayjs
  toDate?: dayjs.Dayjs
  month?: number
  year?: number
  dateRangeValid?: boolean
  /**
   * When false the expensive client-metrics query is suppressed so the tab
   * can be pre-populated from the URL without firing requests before the user
   * actually visits it.
   */
  isActive?: boolean
  // Controlled state (lifted to parent for URL persistence)
  selectedCompanyIds: string[]
  onCompanyIdsChange: (ids: string[]) => void
  selectedCarriers: string[]
  onSelectedCarriersChange: (carriers: string[]) => void
  companyExpanded: boolean
  onCompanyExpandedChange: (v: boolean) => void
  carrierExpanded: boolean
  onCarrierExpandedChange: (v: boolean) => void
}

export default function CustomerMetrics({
  paymentMetricsData,
  loading,
  fromDate,
  toDate,
  month,
  year,
  dateRangeValid = true,
  isActive = true,
  selectedCompanyIds,
  onCompanyIdsChange,
  selectedCarriers,
  onSelectedCarriersChange,
  companyExpanded,
  onCompanyExpandedChange,
  carrierExpanded,
  onCarrierExpandedChange,
}: CustomerMetricsProps) {
  const clientRequest = useMemo(() => {
    // Suppress the query when the tab is not yet active so a URL with
    // pre-selected companies doesn't fire a request while the user is on a
    // different tab.
    if (!isActive || !dateRangeValid || selectedCompanyIds.length === 0) {
      return null
    }
    return {
      ...(fromDate ? { fromDate: startOfDayPST(fromDate) } : {}),
      ...(toDate ? { toDate: endOfDayPST(toDate) } : {}),
      month,
      year,
      clientPublicKeys: selectedCompanyIds,
    }
  }, [
    isActive,
    fromDate,
    toDate,
    dateRangeValid,
    month,
    year,
    selectedCompanyIds,
  ])

  const { data: clientData, isLoading: isClientLoading } =
    useClientMetricsQuery(clientRequest)

  const { data: carrierNames, isLoading: isCarrierNamesLoading } =
    usePaymentCarrierListNameQuery()

  return (
    <div>
      <CompanyPerspectiveMetrics
        data={clientData}
        loading={isClientLoading}
        selectedCompanyIds={selectedCompanyIds}
        onCompanyIdsChange={onCompanyIdsChange}
        expanded={companyExpanded}
        onExpandedChange={onCompanyExpandedChange}
      />

      <CarrierPerspectiveMetrics
        data={paymentMetricsData?.carrierMetrics}
        loading={loading}
        carrierNames={carrierNames ?? []}
        carrierNamesLoading={isCarrierNamesLoading}
        selectedCarriers={selectedCarriers}
        onSelectedCarriersChange={onSelectedCarriersChange}
        expanded={carrierExpanded}
        onExpandedChange={onCarrierExpandedChange}
      />
    </div>
  )
}
